import type { QueryKey } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';

import type {
  DropdownSearchOptions,
  DropdownState,
  ExternalDropdownOption,
  OptionTemplateComponent,
} from '~/chords/DropdownSingleSelect/types';

import { DropdownSingleSelect } from '../DropdownSingleSelect/DropdownSingleSelect';
import { LoadingOptionTemplate } from './LoadingOptionTemplate';

const DISABLED_OPTION_VALUE =
  'disabled_state_id_a0179e78-8f05-4e0e-a9bc-a857e4824b88';

interface BaseProps<T, K> {
  /** The function that performs the search; receives the current search string */
  queryFn: (searchTerm: string) => Promise<T[]>;

  /** The base query key for TanStack Query caching */
  queryKey: QueryKey;

  /** Time in ms to wait after typing stops before triggering the API */
  debounceTimeout?: number;

  /** Function to extract the display string from an option object */
  getOptionLabel: (option: T) => string;

  /** Function to extract the unique identifier/value from an option object */
  getOptionValue: (option: T) => K;

  /** Callback triggered when a user selects an item */
  onChange?: (option: T) => void;

  /** Callback triggered when the dropdown opens or closes */
  onOpenChange?: (isOpen: boolean) => void;

  /** Provide an optional React component to show options with custom styling.
   * For instance, display the country flag next to the country name. */
  optionTemplate?: OptionTemplateComponent;

  /** Custom error message when search fails */
  searchErrorMessage?: string;

  /** Placeholder text for the input field */
  placeholder?: string;

  /** ID for the form control association */
  id?: string;

  /** State for styling */
  state?: DropdownState;

  /** Whether the dropdown is disabled */
  disabled?: boolean;

  searchOptions?: Partial<Pick<DropdownSearchOptions, 'maxLength'>>;

  /**
   * When true, ignores close requests resulting from blur (e.g. switching tabs).
   * Other closes (Escape, selection, click outside) still apply.
   */
  preventCloseOnWindowBlur?: boolean;
}

// Allow specifying a defaultValue by providing either the full object or just the ID with a resolver function.
export type DropdownSingleSelectAsyncProps<T, K> = BaseProps<T, K> &
  (
    | { defaultValue?: T | null; resolveFn?: never } // Full Object
    | {
        defaultValue: K;
        resolveFn: (id: K) => Promise<T>;
        defaultValueErrorMessage?: string;
      } // ID + Resolver
  );

export function DropdownSingleSelectAsync<T, K>(
  props: DropdownSingleSelectAsyncProps<T, K>,
) {
  const {
    defaultValue,
    resolveFn,
    queryFn,
    queryKey,
    debounceTimeout = 200,
    getOptionLabel,
    getOptionValue,
    onChange,
    onOpenChange,
    optionTemplate,
    searchErrorMessage,
    placeholder,
    id,
    state,
    disabled,
    searchOptions: searchOptionsOverrides,
    preventCloseOnWindowBlur,
  } = props;

  const { t } = useTranslation('common');

  // Get defaultValueErrorMessage safely from props if using async resolver
  const defaultValueErrorMessage =
    'defaultValueErrorMessage' in props
      ? props.defaultValueErrorMessage
      : undefined;

  const getOptionLabelRef = useRef(getOptionLabel);
  const getOptionValueRef = useRef(getOptionValue);
  const onChangeRef = useRef(onChange);

  getOptionLabelRef.current = getOptionLabel;
  getOptionValueRef.current = getOptionValue;
  onChangeRef.current = onChange;

  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const baseKey = useMemo(() => [queryKey].flat(), [queryKey]);

  // Resolve initial value
  const resolvedId = useMemo(() => {
    if (defaultValue == null) {
      return null;
    }

    if (resolveFn) {
      return defaultValue;
    }

    return getOptionValue(defaultValue as T);
  }, [defaultValue, resolveFn, getOptionValue]);

  const { data: initialData, isError: isDefaultError } = useQuery({
    queryKey: [...baseKey, 'initial', resolvedId],
    queryFn: () =>
      resolveFn ? resolveFn(defaultValue) : Promise.resolve(defaultValue!),
    enabled: defaultValue != null,
  });

  useEffect(() => {
    if (initialData) {
      setSelectedItem(initialData);
      onChangeRef.current?.(initialData);
    }
  }, [initialData, isDefaultError, defaultValue]);

  // Fetch Results
  useDebounce(() => setDebouncedSearch(inputValue), debounceTimeout, [
    inputValue,
  ]);

  const {
    data: searchResults,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [...baseKey, 'search', debouncedSearch],
    queryFn: () => queryFn(debouncedSearch),
    enabled: isDropdownOpen && !!debouncedSearch,
    refetchOnWindowFocus: false,
  });

  const isDebouncing = debouncedSearch !== inputValue;
  const showError = isError && isDropdownOpen;
  const areResultsOutdated = isDebouncing || !searchResults || isFetching;
  const showLoading = areResultsOutdated && !!inputValue;

  // Map results to options
  const { options, valueToItemMap } = useMemo(() => {
    const results: ExternalDropdownOption[] = [];
    const itemMap = new Map<string, T>();

    if (showError) {
      results.push({
        label: searchErrorMessage || t('failedToLoadData'),
        value: DISABLED_OPTION_VALUE,
        disabled: true,
        isVisible: true,
      });
    } else if (showLoading) {
      results.push({
        label: t('chords:loadingWithEllipsis'),
        value: DISABLED_OPTION_VALUE,
        disabled: true,
        isVisible: true,
      });
    } else {
      results.push(
        ...(searchResults ?? []).map((item) => {
          const value = String(getOptionValueRef.current(item));

          itemMap.set(value, item);

          return {
            label: getOptionLabelRef.current(item),
            value,
            isVisible: true,
          };
        }),
      );
    }

    if (selectedItem) {
      const selectedValue = String(getOptionValueRef.current(selectedItem));
      const isPresentInResults = results.some(
        (opt) => opt.value === selectedValue,
      );

      if (!isPresentInResults) {
        // Prepend the selected item but mark it as hidden
        // This allows DropdownSingleSelect to know what the value is without showing it in the list
        results.unshift({
          label: getOptionLabelRef.current(selectedItem),
          value: selectedValue,
          isVisible: false,
        });
        itemMap.set(selectedValue, selectedItem);
      }
    }

    return { options: results, valueToItemMap: itemMap };
  }, [
    showLoading,
    showError,
    searchResults,
    selectedItem,
    searchErrorMessage,
    t,
  ]);

  const resolvedPlaceholder = useMemo(() => {
    if (isDefaultError && defaultValue != null) {
      return defaultValueErrorMessage || t('failedToLoadData');
    }

    if (selectedItem) {
      return String(getOptionLabelRef.current(selectedItem));
    }

    return placeholder;
  }, [
    isDefaultError,
    defaultValue,
    defaultValueErrorMessage,
    selectedItem,
    t,
    placeholder,
  ]);

  const handleValueChange = (newValue: string) => {
    const newItem = valueToItemMap.get(newValue);

    if (newItem) {
      setSelectedItem(newItem);
      onChangeRef.current?.(newItem);
    }
  };

  return (
    <DropdownSingleSelect
      id={id}
      options={options}
      placeholder={resolvedPlaceholder}
      value={
        selectedItem ? String(getOptionValueRef.current(selectedItem)) : ''
      }
      state={state}
      disabled={disabled}
      preventCloseOnWindowBlur={preventCloseOnWindowBlur}
      onOpenChange={(open) => {
        setIsDropdownOpen(open);

        if (!open) {
          setInputValue('');
          setDebouncedSearch('');
        }

        onOpenChange?.(open);
      }}
      searchOptions={{
        isEnabled: true,
        focusOnOpen: true,
        disableInternalFiltering: true,
        onChange: (val) => {
          setInputValue(val.trim());
        },
        ...searchOptionsOverrides,
      }}
      onValueChange={handleValueChange}
      OptionTemplate={
        showLoading && !showError ? LoadingOptionTemplate : optionTemplate
      }
    />
  );
}

DropdownSingleSelectAsync.displayName = 'DropdownSingleSelectAsync';
